import React, { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { Calendar, Check, Hash, MapPin, Sparkles, TrendingUp, User, Users, Video } from 'lucide-react';
import {
  Event,
  Post,
  UserProfile,
  convertTimestampToDate,
  getUserProfileByUid,
  subscribeToEvents,
  subscribeToUsers
} from '../../firebase/firestore';
import { sendFriendRequest, subscribeToFriends, Friend } from '../../firebase/friends';
import { useAuth } from '../../contexts/AuthContext';
import { getProfilePath, formatHandle } from '../../utils/formatHandle';

interface RightRailProps {
  posts?: Post[];
  className?: string;
}

/** Rotates once per day; kinship-flavored, not generic corporate quotes. */
const DAILY_INSPIRATION = [
  { quote: 'We are all related. What happens to one of us happens to all of us.', source: 'Lakota teaching' },
  { quote: 'It takes a village to raise a child.', source: 'African proverb' },
  { quote: 'The land knows you, even when you are lost.', source: 'Robin Wall Kimmerer' },
  { quote: 'In every deliberation, we must consider the impact on the seventh generation.', source: 'Haudenosaunee principle' },
  { quote: 'Alone we can do so little; together we can do so much.', source: 'Helen Keller' },
  { quote: 'The fire is the center of the home, and the story is the center of the fire.', source: 'Oral tradition' },
  { quote: 'Walk gently on the earth, for the earth is our first mother.', source: 'Indigenous teaching' }
];

const railCard = 'rounded-3xl border border-dark-border/70 bg-dark-card p-5 shadow-soft';

const railHeading = (icon: React.ReactNode, title: string, to?: string, linkLabel?: string) => (
  <div className="mb-4 flex items-center justify-between">
    <h3 className="flex items-center gap-2 text-[15px] font-bold text-text-primary">
      {icon}
      {title}
    </h3>
    {to && (
      <Link to={to} className="text-xs font-semibold text-accent-blue transition-colors hover:text-accent-strong">
        {linkLabel || 'View all'}
      </Link>
    )}
  </div>
);

const RightRail: React.FC<RightRailProps> = ({ posts = [], className = '' }) => {
  const { user } = useAuth();
  const [events, setEvents] = useState<Event[]>([]);
  const [members, setMembers] = useState<UserProfile[]>([]);
  const [friends, setFriends] = useState<Friend[]>([]);
  const [myProfile, setMyProfile] = useState<UserProfile | null>(null);
  const [sentTo, setSentTo] = useState<Set<string>>(new Set());

  useEffect(() => {
    const unsubEvents = subscribeToEvents(setEvents);
    const unsubUsers = subscribeToUsers(setMembers);
    return () => {
      unsubEvents();
      unsubUsers();
    };
  }, []);

  useEffect(() => {
    if (!user) {
      setFriends([]);
      setMyProfile(null);
      return;
    }
    const unsubFriends = subscribeToFriends(user.uid, setFriends);
    getUserProfileByUid(user.uid)
      .then(setMyProfile)
      .catch((error) => console.error('Error loading rail profile:', error));
    return () => unsubFriends();
  }, [user]);

  const upcomingEvents = useMemo(() => {
    const now = new Date();
    return events
      .filter((event) => event.isActive !== false && convertTimestampToDate(event.startDate) >= now)
      .sort(
        (a, b) =>
          convertTimestampToDate(a.startDate).getTime() - convertTimestampToDate(b.startDate).getTime()
      )
      .slice(0, 3);
  }, [events]);

  const suggestedMembers = useMemo(() => {
    const friendIds = new Set(friends.map((friend) => friend.friendId));
    return members
      .filter((member) => member.uid !== user?.uid && !member.isBanned && !friendIds.has(member.uid))
      .slice(0, 3);
  }, [members, friends, user?.uid]);

  const trendingTopics = useMemo(() => {
    const counts = new Map<string, number>();
    posts.forEach((post) => {
      (post.tags || []).forEach((tag) => {
        const key = tag.trim().toLowerCase();
        if (key) counts.set(key, (counts.get(key) || 0) + 1);
      });
      const category = post.category?.trim().toLowerCase();
      if (category) counts.set(category, (counts.get(category) || 0) + 1);
    });
    return Array.from(counts.entries())
      .sort((a, b) => b[1] - a[1])
      .slice(0, 5);
  }, [posts]);

  const inspiration = useMemo(() => {
    const dayOfYear = Math.floor(
      (Date.now() - new Date(new Date().getFullYear(), 0, 0).getTime()) / 86400000
    );
    return DAILY_INSPIRATION[dayOfYear % DAILY_INSPIRATION.length];
  }, []);

  const handleFollow = async (member: UserProfile) => {
    if (!user || sentTo.has(member.uid)) return;
    setSentTo((prev) => new Set(prev).add(member.uid));
    try {
      await sendFriendRequest(
        user.uid,
        member.uid,
        myProfile?.displayName || user.displayName || 'A community member',
        myProfile?.handle || '',
        myProfile?.profileImageUrl
      );
    } catch (error) {
      console.error('Error sending friend request:', error);
      setSentTo((prev) => {
        const next = new Set(prev);
        next.delete(member.uid);
        return next;
      });
    }
  };

  return (
    <aside className={`space-y-5 ${className}`}>
      {/* Suggested for you */}
      <div className={railCard}>
        {railHeading(<Users className="h-4 w-4 text-accent-green" />, 'Suggested for You', '/community-members')}
        {suggestedMembers.length === 0 ? (
          <p className="text-sm text-text-muted">New members will appear here.</p>
        ) : (
          <ul className="space-y-3">
            {suggestedMembers.map((member) => (
              <li key={member.id} className="flex items-center gap-3">
                <Link to={getProfilePath(member.uid, member.handle)} className="flex-shrink-0">
                  {member.profileImageUrl ? (
                    <img
                      src={member.profileImageUrl}
                      alt=""
                      className="h-10 w-10 rounded-full object-cover"
                    />
                  ) : (
                    <span className="grid h-10 w-10 place-items-center rounded-full bg-accent-green/15">
                      <User className="h-4 w-4 text-accent-green" />
                    </span>
                  )}
                </Link>
                <Link to={getProfilePath(member.uid, member.handle)} className="group min-w-0 flex-1">
                  <span className="block truncate text-sm font-semibold text-text-primary group-hover:text-accent-blue">
                    {member.displayName}
                  </span>
                  {member.handle && (
                    <span className="block truncate text-xs text-text-muted">
                      {formatHandle(member.handle)}
                    </span>
                  )}
                </Link>
                {user ? (
                  <button
                    type="button"
                    onClick={() => handleFollow(member)}
                    disabled={sentTo.has(member.uid)}
                    className={`flex-shrink-0 rounded-full px-3.5 py-1.5 text-xs font-semibold transition-all duration-200 ease-premium ${
                      sentTo.has(member.uid)
                        ? 'bg-accent-green/15 text-accent-green'
                        : 'border border-accent-blue/50 text-accent-blue hover:bg-accent-blue hover:text-white'
                    }`}
                  >
                    {sentTo.has(member.uid) ? (
                      <span className="flex items-center gap-1">
                        <Check className="h-3 w-3" /> Sent
                      </span>
                    ) : (
                      'Follow'
                    )}
                  </button>
                ) : (
                  <Link
                    to="/login"
                    className="flex-shrink-0 rounded-full border border-accent-blue/50 px-3.5 py-1.5 text-xs font-semibold text-accent-blue transition-colors hover:bg-accent-blue hover:text-white"
                  >
                    Follow
                  </Link>
                )}
              </li>
            ))}
          </ul>
        )}
      </div>

      {/* Trending topics */}
      <div className={railCard}>
        {railHeading(<TrendingUp className="h-4 w-4 text-accent-purple" />, 'Trending Topics')}
        {trendingTopics.length === 0 ? (
          <p className="text-sm text-text-muted">
            Topics trend as the community posts.{' '}
            <Link to="/cultural-posts" className="font-semibold text-accent-blue hover:underline">
              Start one →
            </Link>
          </p>
        ) : (
          <ul className="space-y-1">
            {trendingTopics.map(([topic, count]) => (
              <li key={topic}>
                <Link
                  to="/global-feed"
                  className="group flex items-center gap-3 rounded-2xl p-2 -m-1 transition-colors hover:bg-surface-sunken"
                >
                  <span className="grid h-8 w-8 flex-shrink-0 place-items-center rounded-xl bg-accent-purple/10">
                    <Hash className="h-4 w-4 text-accent-purple" />
                  </span>
                  <span className="min-w-0">
                    <span className="block truncate text-sm font-semibold capitalize text-text-primary group-hover:text-accent-blue">
                      #{topic.replace(/\s+/g, '')}
                    </span>
                    <span className="block text-xs text-text-muted">
                      {count} {count === 1 ? 'post' : 'posts'}
                    </span>
                  </span>
                </Link>
              </li>
            ))}
          </ul>
        )}
      </div>

      {/* Upcoming events */}
      <div className={railCard}>
        {railHeading(<Calendar className="h-4 w-4 text-accent-blue" />, 'Upcoming Events', '/events')}
        {upcomingEvents.length === 0 ? (
          <p className="text-sm text-text-muted">
            No events on the horizon yet.{' '}
            <Link to="/events" className="font-semibold text-accent-blue hover:underline">
              Plan one →
            </Link>
          </p>
        ) : (
          <ul className="space-y-3">
            {upcomingEvents.map((event) => {
              const start = convertTimestampToDate(event.startDate);
              return (
                <li key={event.id}>
                  <Link
                    to="/events"
                    className="group flex items-start gap-3 rounded-2xl p-2 -m-2 transition-colors hover:bg-surface-sunken"
                  >
                    {event.imageUrl ? (
                      <img
                        src={event.imageUrl}
                        alt=""
                        className="h-11 w-11 flex-shrink-0 rounded-xl object-cover"
                      />
                    ) : (
                      <span className="flex h-11 w-11 flex-shrink-0 flex-col items-center justify-center rounded-xl bg-accent-blue/10 text-accent-blue">
                        <span className="text-[10px] font-bold uppercase leading-none">
                          {start.toLocaleDateString(undefined, { month: 'short' })}
                        </span>
                        <span className="text-base font-extrabold leading-tight">{start.getDate()}</span>
                      </span>
                    )}
                    <span className="min-w-0">
                      <span className="block truncate text-sm font-semibold text-text-primary group-hover:text-accent-blue">
                        {event.title}
                      </span>
                      <span className="mt-0.5 flex items-center gap-1 text-xs text-text-muted">
                        {event.eventType === 'virtual' ? (
                          <Video className="h-3 w-3" />
                        ) : (
                          <MapPin className="h-3 w-3" />
                        )}
                        <span className="truncate">
                          {start.toLocaleDateString(undefined, { weekday: 'short', month: 'short', day: 'numeric' })} ·{' '}
                          {start.toLocaleTimeString(undefined, { hour: 'numeric', minute: '2-digit' })}
                        </span>
                      </span>
                    </span>
                  </Link>
                </li>
              );
            })}
          </ul>
        )}
      </div>

      {/* Daily inspiration */}
      <div className="aurora-bg overflow-hidden rounded-3xl border border-dark-border/70 bg-dark-card p-5 shadow-soft">
        <h3 className="mb-3 flex items-center gap-2 text-[15px] font-bold text-text-primary">
          <Sparkles className="h-4 w-4 text-accent-orange" />
          Daily Inspiration
        </h3>
        <blockquote className="font-serif text-[15px] leading-relaxed text-text-primary">
          “{inspiration.quote}”
        </blockquote>
        <p className="mt-2 text-xs font-medium text-text-muted">— {inspiration.source}</p>
      </div>

      {/* Community pulse */}
      <div className={railCard}>
        {railHeading(<TrendingUp className="h-4 w-4 text-accent-green" />, 'Community Pulse')}
        <dl className="grid grid-cols-3 gap-2">
          <div className="rounded-2xl bg-surface-sunken/70 p-3 text-center">
            <dd className="text-lg font-extrabold text-text-primary">{members.length}</dd>
            <dt className="mt-0.5 text-[11px] font-medium text-text-muted">Members</dt>
          </div>
          <div className="rounded-2xl bg-surface-sunken/70 p-3 text-center">
            <dd className="text-lg font-extrabold text-text-primary">{posts.length}</dd>
            <dt className="mt-0.5 text-[11px] font-medium text-text-muted">Posts</dt>
          </div>
          <div className="rounded-2xl bg-surface-sunken/70 p-3 text-center">
            <dd className="text-lg font-extrabold text-text-primary">{events.length}</dd>
            <dt className="mt-0.5 text-[11px] font-medium text-text-muted">Events</dt>
          </div>
        </dl>
      </div>
    </aside>
  );
};

export default RightRail;
